import { useDevisActif } from '../hooks/useDevisActif';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Testimonials from '../components/Testimonials';
import { TripCard } from '../components/TripCard';
import RestezInformeCTA from '../components/RestezInformeCTA';
import AffichesAccueil from '../components/AffichesAccueil';
import { useStatiquesTextes, TexteRiche } from '../hooks/useFormTextes';
import SpaBloc from '../components/SpaBloc';
import ALaCarteBloc from '../components/ALaCarteBloc';
import VideosYoutube from '../components/VideosYoutube';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const T = useStatiquesTextes(); // Iter 771 — textes de l'accueil éditables
  const devis = useDevisActif();
  const [featuredTrips, setFeaturedTrips] = useState([]);
  const [allTrips, setAllTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, allRes] = await Promise.all([
          axios.get(`${API}/trips/featured`),
          axios.get(`${API}/trips`),
        ]);
        setFeaturedTrips(featuredRes.data.slice(0, 3));
        setAllTrips(allRes.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="relative text-white py-16 md:py-24 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/e9043dd7-9f45-47d8-a9c0-cc7eeeb82181/images/d01d81bc0b1674861ddc4507cc4fc349a80d40e023be82161e8e662a8e203651.png)',
            filter: 'brightness(1.1) contrast(1.1)',
          }}
        >
          {/* Overlay gradient pour lisibilité - opacité réduite pour plus de visibilité */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#5BC5D4]/70 to-[#3BA4B3]/70"></div>
        </div>
        
        {/* Content */}
        <div className="container-custom relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            {/* Logo en grand */}
            <div className="mb-8 fade-in">
              <img 
                src="/images/logo.jpg" 
                alt="Rhône Alpes Amitié Voyages" 
                className="h-32 md:h-40 w-auto mx-auto rounded-2xl shadow-2xl bg-white p-4"
              />
            </div>
            
            {/* Nom de l'association en très gros */}
            <div className="mb-6 fade-in" style={{ animationDelay: '0.2s' }}>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-3 tracking-tight">
                RHÔNE ALPES AMITIÉ
              </h1>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white">
                VOYAGES
              </h2>
            </div>
            
            {/* Slogan */}
            <div className="mb-8 fade-in" style={{ animationDelay: '0.4s' }}>
              <p className="text-2xl md:text-3xl font-semibold mb-4 text-yellow-100" data-testid="home-slogan-1">
                {T('home_slogan_1')}
              </p>
              <p className="text-xl md:text-2xl opacity-95" data-testid="home-slogan-2">
                {T('home_slogan_2')}
              </p>
            </div>
            
            {/* Stats rapides */}
            <div className="grid grid-cols-1 gap-4 max-w-md mx-auto mb-8 fade-in" style={{ animationDelay: '0.6s' }}>
              <div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
                <div className="text-3xl md:text-4xl font-bold">30 ans</div>
                <div className="text-sm md:text-base opacity-90">d'expérience en 2027</div>
              </div>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex flex-wrap justify-center gap-4 fade-in" style={{ animationDelay: '0.8s' }}>
              <Link to="/voyages" onClick={() => window.scrollTo(0, 0)} className="btn bg-white text-[#5BC5D4] hover:bg-gray-100 text-lg px-8 py-4 shadow-2xl">
                🌍 Découvrir nos voyages
              </Link>
              <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer" className="btn text-lg px-8 py-4 shadow-2xl bg-[#25D366] text-white hover:bg-[#1da851]">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                💬 Contactez-nous
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Iter 703 (Kamel) — 🖼️ affiches Espagne 2027 (bloc piloté dans l'admin : Site internet → Accueil) */}
      <AffichesAccueil />
      {/* Iter 710 (Kamel) — 🧖 le spa en vidéo (première vidéo du site), réglé dans Paramètres Espagne */}
      <section className="bg-[#F7F3EA] pb-10 md:pb-14"><div className="container-custom space-y-8"><ALaCarteBloc compact /><SpaBloc compact /></div></section>

      {/* Restez informé — CTA prospects (placé en haut, juste après le hero) */}
      <RestezInformeCTA variant="banner" context="home" />

      {/* BANNIÈRE ESPAGNE – PROJET PHARE (Iter 459 : affichée seulement quand un séjour Lloret est ouvert) */}
      {(() => {
        const tous = allTrips.filter(t => /lloret/i.test(t.title || '') && !/hors forfait/i.test(t.title || '') && !t.is_hidden_public && t.status !== 'completed')
          .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));
        if (!tous.length) return null;
        const annee = (tous[0].start_date || '').slice(0, 4);
        const lloret = tous.filter(t => (t.start_date || '').startsWith(annee));  // Iter 692 — la saison la plus récente publiée
        const court = lloret.find(t => /4 jours|week-end|printemps/i.test(t.title)) || null;
        const long = lloret.find(t => /été|8 jours|séjour/i.test(t.title)) || lloret[0];
        // Iter 692 (Kamel) — le car est une option : on affiche « séjour dès X € · car en option Y €/trajet »
        const legPrice = (t) => t?.transport_option?.price_per_leg || (t?.weekly_pricing || []).find(w => w.transport_price_per_leg)?.transport_price_per_leg || null;
        const fusion = !!(legPrice(long) || legPrice(court));
        return (
      <section className="relative overflow-hidden" data-testid="spain-banner">
        <div className="bg-gradient-to-r from-[#C60B1E] via-[#FFC400] to-[#C60B1E] py-1" />
        <div className="bg-[#1E3A5F] py-10 md:py-14">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 text-center md:text-left">
                <div className="inline-block bg-[#FFC400] text-[#1E3A5F] text-xs font-black px-4 py-1.5 rounded-full mb-4 tracking-wider uppercase">
                  {fusion ? `Espagne ${annee} — les vacances les moins chères de France` : `Le gros projet ${annee}`}
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Lloret del Mar, Espagne
                </h2>
                <p className="text-lg text-gray-300 mb-2" data-testid="spain-banner-sous-titre">
                  {/* Iter 692 (Kamel) — aucune mention du spa tant que l'hôtel n'a pas confirmé la formule ; réactivation en un clic (Paramètres Espagne) */}
                  Pension complète{(Number(annee) < 2027 || devis.spa?.actif) ? <> &bull; {devis.spa?.formule ? `Espace bien-être inclus (${devis.spa.formule})` : 'Spa inclus'}</> : null} &bull; Piscines, salle de sport, animations et spectacles inclus &bull; Saison {annee}
                </p>
                {fusion && (
                  <p className="text-sm text-gray-200 mb-2 max-w-xl leading-relaxed" data-testid="spain-banner-argument">
                    Dans un contexte où le coût de la vie est de plus en plus cher, <strong className="text-[#FFC400]">notre association garde le cap</strong> : séjour à l'hôtel dès <strong>{Math.round(Math.min(...lloret.map(t => t.price_from || 1e9)))} €</strong> par personne, et le car de l'association <strong>en option, {legPrice(long) || legPrice(court)} € par trajet, au départ de la région lyonnaise et de Saint-Étienne</strong> — vous ne payez que ce que vous utilisez.
                  </p>
                )}
                <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-5">
                  {court && (
                    <Link to={`/voyage/${court.id}`} onClick={() => window.scrollTo(0, 0)} className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl px-5 py-3 text-center hover:bg-opacity-20 transition-all cursor-pointer" data-testid="spain-price-4j">
                      <div className="text-xs text-gray-400 uppercase tracking-wide">{court.duration_days ? `${court.duration_days} jours` : 'Court séjour'}{/mai/i.test(`${court.title} ${court.start_date}`) || (court.start_date || '').slice(5, 7) === '05' ? ' · mai' : ''}</div>
                      <div className="text-xl font-bold text-[#FFC400]">séjour dès {Math.round(court.price_from || 0)} &euro;</div>
                      {legPrice(court) && <div className="text-[11px] text-gray-300" data-testid="spain-leg-4j">+ car en option {legPrice(court)} €/trajet</div>}
                    </Link>
                  )}
                  <Link to={`/voyage/${long.id}`} onClick={() => window.scrollTo(0, 0)} className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl px-5 py-3 text-center hover:bg-opacity-20 transition-all cursor-pointer" data-testid="spain-price-8j">
                    <div className="text-xs text-gray-400 uppercase tracking-wide">7 nuits (été)</div>
                    <div className="text-xl font-bold text-[#FFC400]">séjour dès {Math.round(long.price_from || 0)} &euro;</div>
                    {legPrice(long) && <div className="text-[11px] text-gray-300" data-testid="spain-leg-8j">+ car en option {legPrice(long)} €/trajet</div>}
                  </Link>
                </div>
              </div>
              <div className="flex flex-col gap-3 flex-shrink-0">
                {devis.actif && <Link to="/simulateur-espagne" onClick={() => window.scrollTo(0, 0)} className="bg-[#FFC400] text-[#1E3A5F] px-8 py-4 rounded-xl font-black text-lg hover:bg-[#FFD640] transition shadow-lg hover:shadow-xl text-center" data-testid="spain-banner-devis">Calculer mon devis Espagne</Link>}
                <Link to="/inscription" onClick={() => window.scrollTo(0, 0)} className="bg-white bg-opacity-10 border-2 border-white text-white px-8 py-3 rounded-xl font-bold hover:bg-white hover:text-[#1E3A5F] transition text-center" data-testid="spain-banner-inscrire">
                  S'inscrire maintenant
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-[#C60B1E] via-[#FFC400] to-[#C60B1E] py-1" />
      </section>
        );
      })()}

      {/* Featured Trips */}
      <section className="section">
        <div className="container-custom">
          <h2 className="text-4xl font-bold text-center mb-4 text-[#1E3A5F]">{T('home_une_titre')}</h2>
          <p className="text-center text-gray-600 mb-12">{T('home_une_sous_titre')}</p>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredTrips.map((trip, index) => (
                <TripCard key={trip.id} trip={trip} index={index} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/voyages" onClick={() => window.scrollTo(0, 0)} className="btn btn-outline">
              Voir tous nos voyages
            </Link>
          </div>

          {/* Citation philosophie */}
          <div className="max-w-2xl mx-auto mt-14 text-center" data-testid="philosophie-quote-home">
            <p className="text-gray-500 italic leading-relaxed">
              « Chez nous, le voyage est une destination... mais la rencontre reste le plus beau détour. »
            </p>
          </div>
        </div>
      </section>

      {/* Iter 342 — MonBus : le nouvel outil de choix des places (remplace la bannière solo) */}
      <section className="py-10 bg-white" data-testid="monbus-promo-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#1E3A5F] to-[#2a4d7a] rounded-3xl shadow-xl p-8 md:p-10 text-white text-center">
            <div><span className="inline-block bg-[#5BC5D4] text-[#0f2540] text-xs font-bold px-4 py-1 rounded-full mb-5">✨ NOUVEAU — et 100 % gratuit</span></div>
            <div><div className="bg-white rounded-2xl px-6 py-3 inline-block mb-4 shadow-lg">
              <img src="/monbus-logo.png" alt="MonBus" className="h-10 md:h-14 w-auto" data-testid="monbus-logo-home" />
            </div></div>
            <p className="text-xl md:text-2xl font-bold">« Je choisis ma place, je voyage serein »</p>
            <p className="mt-4 text-sm md:text-base text-gray-200 leading-relaxed max-w-xl mx-auto">
              L'angoisse du voyage en moins 😌 : dès votre règlement, choisissez vous-même vos places
              dans le bus depuis votre Espace Adhérent — en famille, entre amis, côté fenêtre…
              <strong> Première arrivée, première servie</strong>, surtout pour les longs trajets comme l'Espagne !
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-7">
              <Link to="/espace-adherent" onClick={() => window.scrollTo(0, 0)} className="btn bg-[#5BC5D4] text-[#0f2540] hover:bg-[#7fd4e0] font-bold" data-testid="monbus-cta-espace">
                🚌 Choisir mes places
              </Link>
              <Link to="/inscription" onClick={() => window.scrollTo(0, 0)} className="btn btn-outline border-white text-white hover:bg-white hover:text-[#1E3A5F]" data-testid="monbus-cta-inscription">
                Réserver un voyage
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Travel With Us */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <h2 className="text-4xl font-bold text-center mb-4 text-[#1E3A5F]">{T('home_pourquoi_titre')}</h2>
          {T('home_pourquoi_texte') && <div className="max-w-3xl mx-auto text-center text-gray-700 mb-10" data-testid="home-pourquoi-texte"><TexteRiche texte={T('home_pourquoi_texte')} /></div>}
          <div className="flex justify-center mb-12">
            <img
              src="/images/pourquoi-voyager.jpeg"
              alt="Pourquoi voyager avec Rhône Amitié Voyage"
              className="max-w-full h-auto rounded-xl shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* Iter 860 — chaîne YouTube : 3 dernières vidéos */}
      <VideosYoutube titre="Nos vidéos de voyage" limit={3} />

      <Testimonials />

      {/* CTA Section */}
      <section className="section bg-gradient-to-r from-[#5BC5D4] to-[#3BA4B3] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-4">{T('home_cta_titre')}</h2>
          <p className="text-xl mb-8 opacity-90">{T('home_cta_texte')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/inscription" onClick={() => window.scrollTo(0, 0)} className="btn bg-white text-[#5BC5D4] hover:bg-gray-100">
              S'inscrire maintenant
            </Link>
            <Link to="/moi" onClick={() => window.scrollTo(0, 0)} className="btn btn-outline border-white text-white hover:bg-white hover:text-[#5BC5D4]">
              Mon espace adhérent
            </Link>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button - Fixed position bottom left */}
      <a
        href="https://wa.me/33660416218"
        target="_blank"
        rel="noopener noreferrer"
        className="hidden lg:block fixed bottom-6 left-6 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:bg-[#20BA5A] transition-all duration-300 z-40 hover:scale-110"
        title="Contactez-nous sur WhatsApp"
      >
        <svg fill="currentColor" viewBox="0 0 24 24" className="w-8 h-8">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default Home;